export type Service = {
  slug: string
  name: string
  shortName: string
  tagline: string
  whatItIs: string
  problems: string[]
  deliverables: string[]
  who: string
  projectTypes: string[]
}

export const services: Service[] = [
  {
    slug: 'product-and-web',
    name: 'Product & Web',
    shortName: 'Product & Web',
    tagline: 'Websites, web applications, platforms, and digital experiences.',
    whatItIs:
      'Design and development of the digital products your business runs on — from marketing websites to full web applications.',
    problems: [
      'An outdated website that no longer represents the business',
      'A web application that needs to be designed and built from scratch',
      'A platform that has outgrown its original architecture',
    ],
    deliverables: [
      'Websites and marketing platforms',
      'Web applications and customer portals',
      'Design systems and front-end architecture',
    ],
    who: 'Businesses and product teams that need a website or web application designed and built properly.',
    projectTypes: ['Corporate and product websites', 'Web applications', 'Customer and partner portals'],
  },
  {
    slug: 'software',
    name: 'Software',
    shortName: 'Software',
    tagline: 'Custom business software, internal tools, dashboards, workflow systems, and automation.',
    whatItIs:
      'Software built around how an organization actually operates — replacing manual processes and disconnected tools with a single system.',
    problems: [
      'Manual processes that depend on spreadsheets or paper',
      'Information scattered across disconnected tools',
      'Operational bottlenecks that slow the business down',
    ],
    deliverables: [
      'Internal tools and admin dashboards',
      'Workflow and process automation',
      'Custom business systems',
    ],
    who: 'Organizations whose operations have outgrown generic software or manual processes.',
    projectTypes: ['Internal dashboards', 'Workflow systems', 'Process automation tools'],
  },
  {
    slug: 'mobile',
    name: 'Mobile',
    shortName: 'Mobile',
    tagline: 'Mobile applications designed around real users and real workflows.',
    whatItIs:
      'Mobile applications designed and built for the way people actually use them — customers, teams, or both.',
    problems: [
      'A business that needs to reach customers on mobile',
      'A team that needs mobile access to internal systems',
      'A product idea that is mobile-first by nature',
    ],
    deliverables: ['iOS and Android applications', 'Cross-platform mobile apps', 'Mobile product design'],
    who: 'Businesses and product teams building a mobile experience for customers or internal teams.',
    projectTypes: ['Customer-facing apps', 'Internal mobile tools', 'Mobile product MVPs'],
  },
  {
    slug: 'ai-and-intelligence',
    name: 'AI & Intelligence',
    shortName: 'AI & Intelligence',
    tagline: 'AI-enabled products, data systems, research, analytics, and digital intelligence.',
    whatItIs:
      'Practical application of AI and data to real business problems — not novelty, but systems that make information more useful.',
    problems: [
      'Data that exists but is not being used effectively',
      'Manual work that could be assisted or automated intelligently',
      'A product idea that depends on AI or data at its core',
    ],
    deliverables: ['AI-enabled product features', 'Data and analytics systems', 'Applied research and prototyping'],
    who: 'Organizations exploring how AI and data can be applied responsibly to a real problem.',
    projectTypes: ['AI-assisted product features', 'Analytics and reporting systems', 'Data infrastructure'],
  },
  {
    slug: 'brand-and-digital-identity',
    name: 'Brand & Digital Identity',
    shortName: 'Brand & Identity',
    tagline: 'Brand systems and digital experiences that help organizations communicate clearly.',
    whatItIs:
      'Brand and identity work grounded in how an organization will actually be experienced — digitally, primarily.',
    problems: [
      'A brand identity that no longer matches the business',
      'Inconsistent communication across digital touchpoints',
      'A new venture that needs an identity from the ground up',
    ],
    deliverables: ['Brand identity systems', 'Digital design systems', 'Visual and verbal guidelines'],
    who: 'Businesses that need a clear, consistent identity across their digital presence.',
    projectTypes: ['Brand identity', 'Design systems', 'Digital brand guidelines'],
  },
  {
    slug: 'product-strategy',
    name: 'Product Strategy',
    shortName: 'Strategy',
    tagline: 'Discovery, research, prototyping, product planning, and technical strategy.',
    whatItIs:
      'The thinking work that happens before anything gets built — understanding the problem well enough to solve it correctly.',
    problems: [
      'An idea that has not been validated or scoped',
      'Uncertainty about what to build first',
      'A need for technical direction before committing resources',
    ],
    deliverables: ['Discovery and research', 'Prototypes and product roadmaps', 'Technical strategy and scoping'],
    who: 'Founders and teams who need clarity before committing to a build.',
    projectTypes: ['Product discovery', 'Prototyping', 'Technical due diligence'],
  },
]

export function getServiceBySlug(slug: string) {
  return services.find((service) => service.slug === slug)
}
